/**
 * ₹-prefixed, Indian-grouped currency formatting, with an optional decimal-
 * places override.
 *
 * Shared by GenericInvoicePreview.vue and WizardLineItemsStep.vue, which
 * both independently defined the exact same `formatCurrency` method before
 * this was extracted — kept as a tiny standalone utility (same convention as
 * the other src/utils/invoice-*.js helpers) rather than a mixin, since it's
 * a single pure function with no component state involved.
 * (2026-07-22: originally shared with the now-deleted, orphaned
 * InvoiceLineItemsSection.vue — WizardLineItemsStep.vue superseded it.)
 *
 * Also exports line-item cell helpers used by GenericInvoicePreview so every
 * invoice style formats currency / % / numbers the same way from the live
 * column config (type + key), instead of each skin hard-coding rules.
 *
 * @param {number|string} val       – the amount to format
 * @param {number}        decimals  – decimal places to show (defaults to 2,
 *                                     matching the original always-2-decimal
 *                                     behavior). Pass 0 for whole-rupee
 *                                     display — see the "Show decimals"
 *                                     toggle wired through InvoiceCreateManual.vue.
 */

/** Keys that are money even when `type` is missing or loosely set to `computed`. */
const MONEY_KEYS = new Set([
  'amount',
  'baseAmount',
  'unitPrice',
  'rate',
  'rateCard',
  'ctc',
  'monthToDayConversion',
  'taxableValue',
  'igstAmount',
  'lineTotal',
  'payoutAmount',
  'monthlyRate',
])

/** Qty / ratio / day-count columns must never get a ₹ prefix. */
const NON_MONEY_KEYS = new Set([
  'quantity',
  'qty',
  'manMonths',
  'manMonth',
  'qtyManMonths',
  'presentDays',
  'presentDay',
  'totalHeadCount',
  'headCount',
  'dayToMonthRatio',
  'payoutRate',
  'srNo',
  'materialCode',
])

/**
 * @param {number|string} val
 * @param {number}        decimals
 * @param {{ showCurrencySymbol?: boolean, useThousandSeparators?: boolean }} [opts]
 * @returns {string}
 */
export function formatCurrency(val, decimals = 2, opts = {}) {
  const showSymbol = opts.showCurrencySymbol !== false
  const useCommas = opts.useThousandSeparators !== false
  const n = parseFloat(val)
  const d = Number.isInteger(decimals) && decimals >= 0 ? decimals : 2
  const safe = Number.isNaN(n) ? 0 : n
  const body = useCommas
    ? safe.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
    : safe.toFixed(d)
  return showSymbol ? `₹${body}` : body
}

/**
 * Plain number with Indian grouping (no ₹). Whole numbers stay whole unless
 * `decimals` is explicitly provided.
 *
 * @param {number|string} val
 * @param {number|null}   decimals
 * @param {{ useThousandSeparators?: boolean }} [opts]
 * @returns {string}
 */
export function formatNumber(val, decimals = null, opts = {}) {
  if (val == null || val === '') return '—'
  const n = Number(val)
  if (!Number.isFinite(n)) return String(val)
  const useCommas = opts.useThousandSeparators !== false
  if (decimals == null) {
    if (!useCommas) {
      return Number.isInteger(n) ? String(n) : String(n)
    }
    return Number.isInteger(n)
      ? n.toLocaleString('en-IN')
      : n.toLocaleString('en-IN', { maximumFractionDigits: 4 })
  }
  const d = Number.isInteger(decimals) && decimals >= 0 ? decimals : 0
  if (!useCommas) return n.toFixed(d)
  return n.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
}

/**
 * @param {number|string} val
 * @returns {string}
 */
export function formatPercentage(val) {
  if (val == null || val === '') return '—'
  const n = Number(val)
  if (!Number.isFinite(n)) return String(val)
  return `${n}%`
}

/**
 * @param {{ key?: string, type?: string, format?: string, showCurrency?: boolean }} col
 * @returns {boolean}
 */
export function isLineItemMoneyColumn(col = {}) {
  const type = col.type
  const key = String(col.key || '')
  const keyNorm = key.toLowerCase().replace(/[^a-z0-9]/g, '')

  if (col.showCurrency === false || col.format === 'number') return false
  if (NON_MONEY_KEYS.has(key) || /^(qty|quantity|manmonth|presentday|headcount|ratio|srno|materialcode)/.test(keyNorm)) {
    return false
  }
  if (type === 'percentage' || type === 'number' || type === 'text' || type === 'date' || type === 'sac-select') {
    return false
  }
  if (type === 'currency') return true
  if (MONEY_KEYS.has(key)) return true
  // Computed amount-like columns stay money; qty/man-month computed cols are
  // excluded above via NON_MONEY_KEYS / key patterns.
  if (type === 'computed') {
    if (/qty|quantity|manmonth|ratio|day|count/.test(keyNorm)) return false
    if (/amount|price|rate|total|ctc|payout|taxable/.test(keyNorm)) return true
    // Unknown computed: treat as number (no ₹) so Qty(Man Months) never
    // incorrectly shows a currency sign.
    return false
  }
  return false
}

/**
 * True when the column is numeric-ish (money, qty, %, rates) — right-align +
 * single-line values in the invoice items table.
 *
 * @param {{ key?: string, type?: string }} col
 * @returns {boolean}
 */
export function isLineItemNumericColumn(col = {}) {
  if (!col) return false
  if (isLineItemMoneyColumn(col)) return true
  return col.type === 'number' || col.type === 'percentage' || col.type === 'computed'
}

/**
 * Format one line-item cell for preview/PDF from the live column descriptor.
 *
 * @param {*} val
 * @param {{ key?: string, type?: string }} col
 * @param {{ decimals?: number, showCurrencySymbol?: boolean, useThousandSeparators?: boolean }} [opts]
 * @returns {string|*}
 */
export function formatLineItemCellValue(val, col = {}, opts = {}) {
  const decimals = opts.decimals != null ? opts.decimals : 2
  const type = col.type != null && col.type !== '' ? String(col.type).trim().toLowerCase() : ''
  const fmtOpts = {
    showCurrencySymbol: opts.showCurrencySymbol,
    useThousandSeparators: opts.useThousandSeparators,
  }

  if (type === 'percentage') return formatPercentage(val)
  if (isLineItemMoneyColumn(col)) return formatCurrency(val, decimals, fmtOpts)
  if (type === 'number' || type === 'computed') {
    return formatNumber(val, decimals, fmtOpts)
  }

  if (val == null || val === '') return '—'
  return val
}
